import * as React from "react";
import styled from "@emotion/styled";
import { useLocation, useParams } from "react-router-dom";
import {
  AppError,
  Avatar,
  Box,
  Container,
  FlexRow,
  H2,
  H3,
  HeadingGroup,
  IconInstagram,
  IconLink,
  IconTwitter,
  Link,
  LoadingSpinner,
  SocialIconLink,
  Stack,
  Text,
} from "atoms";
import brush from "assets/images/hug-brush-1.svg";
import placeholderAvatar from "assets/images/default-avatar.svg";
import { useAuth } from "features/auth";
import { Head } from "features/misc";
import { Feed, PostCard, useFeed, usePost } from "features/posts";
import { useArtist } from "features/reviewable";
import { ArtistPostHeading } from "features/reviewable/components/ArtistPostHeading";
import { FollowButton } from "features/follows";
import { PostAdmin } from "features/posts/components/PostAdmin";
import { trackEvent } from "contexts/mixpanel";

const trackSignIn = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Artist Post",
  });

const trackSignUp = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: "Artist Post",
  });

type RouteParams = { artistId: string; postId: string };

function ArtistPost() {
  const {
    user,
    roles: { showAdminFeatures },
  } = useAuth();
  const { artistId, postId } = useParams<RouteParams>();
  const { data, isLoading, isError, error } = usePost(postId);
  const { data: artist } = useArtist(artistId);

  // Get other posts by this artist
  const { data: artistFeed } = useFeed(
    new URLSearchParams({ artistId: artistId ?? "", perPage: "5" }),
  );
  // Just in case, filter out the post we're already looking at
  const artistFeedPosts = React.useMemo(
    () =>
      artistFeed?.pages?.[0].data.filter((p) => p.id !== postId).slice(0, 4) ??
      [],
    [artistFeed, postId],
  );

  // Also get posts by other artists, same query as ViewAllUpdates
  const { data: genericFeed } = useFeed();
  // And filter out this current artist
  const genericFeedPosts = React.useMemo(
    () =>
      genericFeed?.pages?.[0].data
        .filter((p) => !p.destinations.find((dest) => dest.feedId === artistId))
        .slice(0, 4) ?? [],
    [genericFeed, artistId],
  );

  const location = useLocation();

  // Is the signed in user the creator of the post?
  const isPostOwner = user?.id && data?.createdByUserId === user.id;

  // Is it a manifold post for an artist claimed by the signed in user?
  const isManifoldAboutUser =
    artist?.adminUsers?.find((uid) => uid === user?.id) && data?.automatedPost;

  // Should we display the admin area?
  const showAdmin =
    artist && data && (showAdminFeatures || isPostOwner || isManifoldAboutUser);

  return (
    <PermalinkWrapper>
      {artist && data ? (
        <Head
          title={`${data.title} | ${artist.name} | HUG `}
          description={data.text}
          socialTitle={`${data.title} by ${artist.name}`}
          socialDescription={data.text}
          socialImage={
            data.image?.url ??
            artist.cardImage?.url ??
            artist.artistCollections?.[0]?.collectionImage?.url ??
            artist.heroImage?.url ??
            placeholderAvatar
          }
          socialImageAltText={`Artwork by ${artist.name}`}
          username={artist.name}
        />
      ) : (
        <Head title="Artist Post | HUG" />
      )}

      <Container>
        {isLoading && <LoadingSpinner />}

        <PostGrid>
          {data ? (
            <Post
              post={data}
              artistHeader={
                <ArtistPostHeading
                  artistId={artist?.id || ""}
                  artistName={data.artistName}
                  artistIcon={data.artistIcon}
                />
              }
              isPermalink
            />
          ) : (
            <NoPost>
              <H2>This update can&apos;t be found</H2>
              {isError && (
                <AppError>
                  {error instanceof Error
                    ? error.message
                    : "Couldn't load post"}
                </AppError>
              )}
            </NoPost>
          )}

          {!user && (
            <FollowCallout>
              <H3 size="sm">
                Sign in to follow {artist?.name ?? <>this&nbsp;artist</>}
              </H3>
              <Text size="xs">
                You must have an account and be signed in to get more updates
                from&nbsp;artists.
              </Text>
              <FlexRow justifyContent="center" gap="10px" itemsFlex="1 0 auto">
                <InvertedLink
                  to="/sign-in"
                  variant="primary"
                  size="xs"
                  state={{ from: location }}
                  onClick={trackSignIn}
                >
                  Sign in
                </InvertedLink>
                <InvertedLink
                  to="/register"
                  variant="primary"
                  size="xs"
                  state={{ from: location }}
                  onClick={trackSignUp}
                >
                  Create Account
                </InvertedLink>
              </FlexRow>
            </FollowCallout>
          )}

          {artist && (
            <ArtistInfo>
              <Stack gap="sm">
                <Stack gap="xs">
                  <Link to={`/artists/${artistId}`} aria-hidden tabIndex={-1}>
                    <SAvatar
                      size={80}
                      src={artist.heroImage.url300 ?? artist.heroImage.url}
                      aria-hidden
                    />
                  </Link>
                  <H3 size="md">
                    <Link to={`/artists/${artistId}`}>{artist.name}</Link>
                  </H3>
                </Stack>

                {(artist.webSiteURL ||
                  artist.instagramHandle ||
                  artist.twitterHandle) && (
                  <FlexRow justifyContent="center" gap="10px">
                    {artist.webSiteURL && (
                      <SocialIconLink
                        artistName={artist.name}
                        href={artist.webSiteURL}
                        aria-label={`Website for ${artist.name}`}
                        target="_blank"
                        linkType="website"
                        iconOnly
                      >
                        <IconLink />
                      </SocialIconLink>
                    )}
                    {artist.instagramHandle && (
                      <SocialIconLink
                        artistName={artist.name ?? ""}
                        href={`https://instagram.com/${artist.instagramHandle}`}
                        aria-label={`${artist.name} on Instagram`}
                        target="_blank"
                        linkType="instagram"
                        iconOnly
                      >
                        <IconInstagram />
                      </SocialIconLink>
                    )}
                    {artist.twitterHandle && (
                      <SocialIconLink
                        artistName={artist.name ?? ""}
                        href={`https://twitter.com/${artist.twitterHandle}`}
                        aria-label={`${artist.name} on Twitter`}
                        target="_blank"
                        linkType="twitter"
                        iconOnly
                      >
                        <IconTwitter />
                      </SocialIconLink>
                    )}
                  </FlexRow>
                )}

                <Stack gap="xs">
                  <SFollowButton artistId={artist?.id || ""} size="xxs" />
                  <Text size="xxxs">
                    Follow this artist to personalize your feed and subscribe to
                    their updates via&nbsp;email.
                  </Text>
                </Stack>
              </Stack>
            </ArtistInfo>
          )}
          <PostBg />
          {showAdmin && <PostAdmin artist={artist} post={data} />}
        </PostGrid>
      </Container>

      <Container width="lg">
        <Stack gap="lg">
          {artistFeedPosts.length > 0 && (
            <Stack gap="sm">
              <FlexRow justifyContent="space-between" alignItems="flex-end">
                <HeadingGroup>
                  <small>Artist Updates</small>
                  <H2 size="h3">Hear more from this artist</H2>
                </HeadingGroup>
                <Link
                  to={`/artists/${artistId}/updates`}
                  variant="primary"
                  size="xs"
                >
                  View All Updates
                </Link>
              </FlexRow>

              <SFeed posts={artistFeedPosts} />
            </Stack>
          )}

          <Stack gap="sm">
            <FlexRow justifyContent="space-between" alignItems="flex-end">
              <HeadingGroup>
                <small>Discover more artists</small>
                <H2 size="h3">Recently Posted from other HUG artists</H2>
              </HeadingGroup>
              <Link to="/artist-updates" variant="primary" size="xs">
                All Artist Updates
              </Link>
            </FlexRow>

            <SFeed posts={genericFeedPosts} showArtist />
          </Stack>
        </Stack>
      </Container>
    </PermalinkWrapper>
  );
}

const PermalinkWrapper = styled.div({
  paddingBottom: "5rem",
  ".Container+.Container": {
    position: "relative",
    zIndex: 2,
  },
});

const PostGrid = styled.div(({ theme }) => ({
  display: "grid",
  padding: "3rem 0 5rem",
  gap: `1rem ${theme.spacing.gutter}`,
  gridTemplateColumns: "1fr",
  gridTemplateRows: "repeat(3, auto)",
  gridTemplateAreas: `
    "post"
    "follow"
    "artist"
    "admin"
  `,
  [theme.breakpoints.desktop]: {
    gap: `1.5rem ${theme.spacing.gutter}`,
    justifyContent: "center",
    gridTemplateColumns: "minmax(440px, 600px) minmax(175px, 325px)",
    gridTemplateRows: "auto auto 1fr",
    gridTemplateAreas: `
      "post artist"
      "post follow"
      "post admin"
      "post bg"
    `,
  },
  "&&>*": {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
  },
}));

const Post = styled(PostCard)({
  gridArea: "post",
  maxHeight: "none",
  fontSize: 15,
  position: "relative",
  zIndex: 2,
});

const NoPost = styled(Box)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  position: "relative",
  zIndex: 2,
}));

const FollowCallout = styled(Box)(({ theme }) => ({
  gridArea: "follow",
  backgroundColor: theme.colors.fg,
  color: theme.colors.bg,
  padding: "2rem 1rem",
  textAlign: "center",
  "h1,h2,h3,h4": { color: theme.colors.bg },
  position: "relative",
  zIndex: 2,
}));

const ArtistInfo = styled(Box)({
  gridArea: "artist",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  position: "relative",
  overflow: "visible",
  zIndex: 2,
  a: {
    textDecoration: "none",
  },
});

const PostBg = styled.div(({ theme }) => ({
  gridArea: "bg",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "100vh",
  backgroundRepeat: "no-repeat",
  backgroundImage: `url("${brush}")`,
  backgroundSize: "cover",
  backgroundPosition: "bottom",
  maxWidth: "none !important",
  pointerEvents: "none",
  opacity: 0.05,
  zIndex: 0,
  [theme.breakpoints.desktopMedium]: {
    backgroundSize: "140vmax",
  },
}));

const SAvatar = styled(Avatar)({
  display: "block",
  margin: "0 auto",
});

const InvertedLink = styled(Link)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  borderColor: theme.colors.bg,
  "&&": {
    color: theme.colors.fg,
  },
  "&:hover, &:focus": {
    backgroundColor: theme.colors.fg80,
    borderColor: theme.colors.fg80,
    color: theme.colors.bg,
  },
}));

const SFeed = styled(Feed)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing.gap,
  "@media (min-width: 720px)": {
    gridTemplateColumns: `repeat(2, calc(50% - (${theme.spacing.gap} / 2)))`,
  },
  [theme.breakpoints.desktopLarge]: {
    gridTemplateColumns: `repeat(4, calc(25% - (${theme.spacing.gap} * .75)))`,
  },
}));

const SFollowButton = styled(FollowButton)({
  margin: "0 auto",
  '&[aria-label="Unfollow"] + *': {
    display: "none",
  },
});

export default ArtistPost;
